import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
    makeStyles,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    InputAdornment,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Grid,
    Typography,
    Box,
    Tooltip,
    CircularProgress
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../TableRowSkeleton";
import ConfirmationModal from "../ConfirmationModal";
import toastError from "../../errors/toastError";
import AIPromptKnowledgeDialog from "../AIPromptKnowledgeDialog";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        maxHeight: "60vh",
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    uploadInput: {
        display: 'none',
    },
    searchField: {
        marginBottom: theme.spacing(2)
    }
}));

const AIKnowledgeModal = ({ open, onClose, flowId }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [knowledge, setKnowledge] = useState([]);
    const [selectedKnowledge, setSelectedKnowledge] = useState(null);
    const [deletingKnowledge, setDeletingKnowledge] = useState(null);
    const [knowledgeDialogOpen, setKnowledgeDialogOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [pendingFile, setPendingFile] = useState(null);
    const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);

    useEffect(() => {
        if (open) {
            fetchKnowledge();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const fetchKnowledge = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/ai-knowledge", {
                params: { flowId }
            });
            setKnowledge(data || []);
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenKnowledgeDialog = () => {
        setSelectedKnowledge(null);
        setKnowledgeDialogOpen(true);
    };

    const handleCloseKnowledgeDialog = () => {
        setSelectedKnowledge(null);
        setKnowledgeDialogOpen(false);
        fetchKnowledge();
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditKnowledge = (item) => {
        setSelectedKnowledge(item);
        setKnowledgeDialogOpen(true);
    };

    const handleDeleteKnowledge = async (knowledgeId) => {
        try {
            await api.delete(`/ai-knowledge/${knowledgeId}`);
            toast.success("Conocimiento eliminado con éxito");
            fetchKnowledge();
        } catch (err) {
            toastError(err);
        }
        setDeletingKnowledge(null);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Guardar el archivo y mostrar confirmación
        setPendingFile(file);
        setOverwriteConfirmOpen(true);
        e.target.value = null; // Reset input
    };

    const handleConfirmOverwrite = async () => {
        if (!pendingFile) return;

        const formData = new FormData();
        formData.append("file", pendingFile);
        if (flowId) {
            formData.append("flowId", flowId);
        }

        setImporting(true);
        setOverwriteConfirmOpen(false);
        try {
            await api.post("/ai-knowledge/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            toast.success("Catálogo importado con éxito");
            fetchKnowledge();
        } catch (err) {
            toastError(err);
        } finally {
            setImporting(false);
            setPendingFile(null);
        }
    };

    const handleCancelOverwrite = () => {
        setOverwriteConfirmOpen(false);
        setPendingFile(null);
    };

    const filterKnowledge = () => {
        if (!searchParam) return knowledge;
        return knowledge.filter(item =>
            item.title.toLowerCase().includes(searchParam) ||
            item.content.toLowerCase().includes(searchParam)
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Base de Conocimiento IA (RAG)</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <ConfirmationModal
                    title={deletingKnowledge && `¿Eliminar "${deletingKnowledge.title}"`}
                    open={confirmModalOpen}
                    onClose={() => setConfirmModalOpen(false)}
                    onConfirm={() => handleDeleteKnowledge(deletingKnowledge.id)}
                >
                    Esta acción no se puede deshacer. El asistente ya no tendrá acceso a esta información.
                </ConfirmationModal>

                <ConfirmationModal
                    title={i18n.t("flowBuilder.modals.overwriteKnowledgeTitle")}
                    open={overwriteConfirmOpen}
                    onClose={handleCancelOverwrite}
                    onConfirm={handleConfirmOverwrite}
                >
                    {i18n.t("flowBuilder.modals.overwriteKnowledgeMessage")}
                </ConfirmationModal>

                <AIPromptKnowledgeDialog
                    open={knowledgeDialogOpen}
                    onClose={handleCloseKnowledgeDialog}
                    knowledgeId={selectedKnowledge?.id}
                    flowId={flowId}
                />

                <Grid container spacing={2} className={classes.searchField}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            placeholder="Buscar conocimiento..."
                            type="search"
                            value={searchParam}
                            onChange={handleSearch}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ color: "gray" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <Tooltip title="En Tipo escribe 'Manual' o 'Remarketing'. En Adjunto va la ruta del archivo." placement="top" arrow>
                        <Button
                            fullWidth
                            variant="outlined"
                            href="/Plantilla_RAG_Botmix.xlsx"
                            download="Plantilla_RAG_Botmix.xlsx"
                            color="default"
                        >
                            Plantilla
                        </Button>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={4} sm={3}>
                        <input
                            accept=".xlsx, .xls, .csv"
                            className={classes.uploadInput}
                            id="modal-upload-file"
                            type="file"
                            disabled={importing}
                            onChange={handleFileUpload}
                        />
                        <label htmlFor="modal-upload-file">
                            <Button
                                fullWidth
                                variant="contained"
                                component="span"
                                disabled={importing}
                                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                            >
                                {importing ? "Procesando..." : "Importar"}
                            </Button>
                        </label>
                    </Grid>
                    <Grid item xs={4} sm={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleOpenKnowledgeDialog}
                            color="primary"
                        >
                            Agregar
                        </Button>
                    </Grid>
                </Grid>

                <Paper className={classes.mainPaper} variant="outlined">
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Título / Producto</TableCell>
                                <TableCell>Descripción</TableCell>
                                <TableCell align="center">Tipo</TableCell>
                                <TableCell align="center">Adjunto(s)</TableCell>
                                <TableCell align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filterKnowledge().map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.title}</TableCell>
                                    <TableCell style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.content}
                                    </TableCell>
                                    <TableCell align="center">{item.type}</TableCell>
                                    <TableCell align="center">
                                        {item.adjunto && String(item.adjunto).toUpperCase() !== "NULL" && String(item.adjunto).toUpperCase() !== "NAN" && String(item.adjunto).trim() !== "" ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <AttachFileIcon fontSize="small" style={{ color: "gray", marginRight: 2 }} />
                                                <Typography variant="caption">
                                                    {(() => {
                                                        try {
                                                            const p = JSON.parse(item.adjunto);
                                                            return Array.isArray(p) ? p.length : 1;
                                                        } catch {
                                                            return 1;
                                                        }
                                                    })()}
                                                </Typography>
                                            </div>
                                        ) : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleEditKnowledge(item)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => {
                                            setDeletingKnowledge(item);
                                            setConfirmModalOpen(true);
                                        }}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={5} />}
                            {!loading && filterKnowledge().length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">No se encontraron conocimientos.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};


export default AIKnowledgeModal;
